import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { projectFilesService } from '../api/project-files.service';
import { PROJECT_FILE_KEYS } from './queryKeys';
import type { MoveProjectFileInput, RenameProjectFileInput } from '../types';

export function useProjectFileMutations(projectId: string) {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useOrganizationStore();

  const invalidateFiles = () =>
    queryClient.invalidateQueries({
      queryKey: PROJECT_FILE_KEYS.byProject(activeOrganizationId ?? '', projectId),
    });

  const upload = useMutation({
    mutationFn: ({
      file,
      folderId,
      source,
    }: {
      file: File;
      folderId?: string;
      /** Origin marker, e.g. `'whiteboard'`; omitted for uploads from the Files view. */
      source?: string;
    }) => projectFilesService.uploadFile(projectId, file, folderId, source),
    onSuccess: invalidateFiles,
  });

  const remove = useMutation({
    mutationFn: (fileId: string) => projectFilesService.deleteFile(projectId, fileId),
    onSuccess: invalidateFiles,
  });

  const bulkDelete = useMutation({
    mutationFn: (fileIds: string[]) => projectFilesService.bulkDeleteFiles(projectId, fileIds),
    onSuccess: invalidateFiles,
  });

  const move = useMutation({
    mutationFn: ({ fileId, data }: { fileId: string; data: MoveProjectFileInput }) =>
      projectFilesService.moveFile(projectId, fileId, data),
    onSuccess: invalidateFiles,
  });

  const renameFile = useMutation({
    mutationFn: ({ fileId, data }: { fileId: string; data: RenameProjectFileInput }) =>
      projectFilesService.renameFile(projectId, fileId, data),
    onSuccess: invalidateFiles,
  });

  const toggleFavorite = useMutation({
    mutationFn: ({ fileId, isFavorite }: { fileId: string; isFavorite: boolean }) =>
      projectFilesService.toggleFavorite(projectId, fileId, isFavorite),
    onSuccess: invalidateFiles,
  });

  const setVisibility = useMutation({
    mutationFn: ({ fileId, isPrivate }: { fileId: string; isPrivate: boolean }) =>
      projectFilesService.setVisibility(projectId, fileId, isPrivate),
    onSuccess: invalidateFiles,
  });

  return {
    upload,
    remove,
    bulkDelete,
    move,
    renameFile,
    toggleFavorite,
    setVisibility,
  };
}
